import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ListTodo, Plus, Check, Calendar, AlertCircle, X, ChevronRight 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GlobalActionItems() {
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [newItemText, setNewItemText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchActionItems = async () => {
    try {
      setLoading(true);
      // Get action items across all projects
      const response = await axios.get(`${API}/action-items`);
      setActionItems(response.data || []);
    } catch (error) {
      console.error('Error fetching action items:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchActionItems();
    }
  }, [isOpen]);

  const toggleComplete = async (item) => {
    try {
      await axios.put(`${API}/projects/${item.project_id}/action-items/${item.id}`, {
        ...item,
        status: item.status === 'completed' ? 'pending' : 'completed',
        completed_at: item.status === 'completed' ? null : new Date().toISOString()
      });
      fetchActionItems();
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };

  const addQuickItem = async () => {
    if (!newItemText.trim()) return;
    
    try {
      // Add to a "General" project or the most recent project
      await axios.post(`${API}/action-items`, {
        description: newItemText,
        priority: 'medium',
        status: 'pending'
      });
      setNewItemText('');
      setShowAddForm(false);
      fetchActionItems();
      toast.success('Action item added');
    } catch (error) {
      toast.error('Failed to add action item');
    }
  };

  const pendingCount = actionItems.filter(i => i.status !== 'completed').length;
  const highPriorityCount = actionItems.filter(i => i.priority === 'high' && i.status !== 'completed').length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `in ${Math.abs(diffDays)} days`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-gray-500 hover:text-gray-700 relative"
        >
          <ListTodo className="h-5 w-5" />
          {pendingCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#BC5D41] text-white text-xs flex items-center justify-center">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Action Items</h3>
            <div className="flex items-center gap-2">
              {highPriorityCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {highPriorityCount} urgent
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {showAddForm && (
            <div className="mt-2 flex gap-2">
              <Input
                placeholder="Quick add action item..."
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addQuickItem()}
                className="h-8 text-sm"
              />
              <Button size="sm" className="h-8" onClick={addQuickItem}>
                Add
              </Button>
            </div>
          )}
        </div>
        
        <div className="max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
          ) : actionItems.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No action items
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {actionItems.slice(0, 10).map((item) => (
                <div 
                  key={item.id}
                  className={`p-3 hover:bg-gray-50 cursor-pointer ${
                    item.status === 'completed' ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <button
                      onClick={() => toggleComplete(item)}
                      className={`mt-0.5 h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center ${
                        item.status === 'completed' 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {item.status === 'completed' && <Check className="h-3 w-3" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${item.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {item.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {item.project_name && (
                          <span className="text-xs text-gray-400 truncate">
                            {item.project_name}
                          </span>
                        )}
                        {item.due_date && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(item.due_date)}
                          </span>
                        )}
                        {item.priority === 'high' && (
                          <AlertCircle className="h-3 w-3 text-red-500" />
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {actionItems.length > 10 && (
          <div className="p-2 border-t border-gray-100 text-center">
            <Button variant="ghost" size="sm" className="text-xs text-gray-500">
              View all {actionItems.length} items
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
